import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, MessageCircle, ChevronRight, Sparkles,
  Zap, Brain, Heart, Briefcase, TrendingUp, Leaf, CloudRain, Sun,
  Compass, Users, ShieldCheck,
} from 'lucide-react';
import { joinWaitlist } from '../lib/waitlist';

const NAVY = '#0F172A';
const INDIGO = '#4F46E5';
const INDIGO_2 = '#6366F1';
const SLATE_2 = '#6B7280';
const SLATE_3 = '#94A3B8';
const BORDER = '#E2E8F0';
const SOFT_BG = '#F8FAFC';

const WA_NUMBER = '918308595553';
const WA_MSG = "Hi Clinity, I'm interested in collaborating as a wellness expert.";
const WA_URL = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(WA_MSG)}`;

const GRADIENT_STYLE = {
  background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 35%, #8B5CF6 70%, #A855F7 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  WebkitTextFillColor: 'transparent',
};

/* 8 dimensions — matches ExpertsMarketplace palette */
const DIMENSIONS = [
  { key: 'physical',  label: 'Physical',  icon: Zap,        color: '#2563EB', bg: '#DBEAFE' },
  { key: 'mental',    label: 'Mental',    icon: Brain,      color: '#4F46E5', bg: '#E0E7FF' },
  { key: 'emotional', label: 'Emotional', icon: Heart,      color: '#7C3AED', bg: '#F3E8FF' },
  { key: 'career',    label: 'Career',    icon: Briefcase,  color: '#D97706', bg: '#FEF3C7' },
  { key: 'financial', label: 'Financial', icon: TrendingUp, color: '#059669', bg: '#D1FAE5' },
  { key: 'lifestyle', label: 'Lifestyle', icon: Leaf,       color: '#DB2777', bg: '#FCE7F3' },
  { key: 'spiritual', label: 'Spiritual', icon: CloudRain,  color: '#6D28D9', bg: '#EDE9FE' },
  { key: 'self_care', label: 'Self-Care', icon: Sun,        color: '#EA580C', bg: '#FFF7ED' },
];

export default function Collaborate() {
  const navigate = useNavigate();

  const handleCollaborate = () => {
    joinWaitlist({ source: 'collaborate_expert' });
  };

  return (
    <div
      className="min-h-screen"
      data-testid="collaborate-page"
      style={{ background: `linear-gradient(180deg, ${SOFT_BG} 0%, #FFFFFF 100%)`, color: NAVY, fontFamily: 'Manrope, sans-serif' }}
    >
      {/* NAV */}
      <nav className="sticky top-0 z-40 backdrop-blur-xl border-b" style={{ backgroundColor: 'rgba(255,255,255,0.85)', borderColor: BORDER }} data-testid="collaborate-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <a href="/" className="text-lg font-bold no-underline" style={{ color: NAVY, letterSpacing: '-0.02em', fontFamily: 'Outfit, sans-serif' }}>Clinity</a>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5 bg-white"
            style={{ border: `1px solid ${BORDER}`, boxShadow: '0 2px 8px rgba(15,23,42,0.06)' }}
            data-testid="collaborate-nav-back"
          >
            <ChevronRight className="w-4 h-4 rotate-180" /> Back
          </button>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
        {/* HERO */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase" style={{ background: '#EEF2FF', color: INDIGO, letterSpacing: '0.22em' }}>
            <Sparkles className="w-3.5 h-3.5" /> Collaborate With Us
          </div>
          <h1
            className="mt-6 text-[40px] sm:text-[52px] lg:text-[64px] font-bold leading-[1.05]"
            style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.03em', color: NAVY }}
            data-testid="collaborate-headline"
          >
            Collaborate with <span style={GRADIENT_STYLE}>Clinity</span>
          </h1>
          <p
            className="mt-6 text-[16px] sm:text-[18px] leading-[1.7] max-w-2xl mx-auto"
            style={{ color: SLATE_2 }}
            data-testid="collaborate-body"
          >
            Use your expertise in any of the <span style={{ color: NAVY, fontWeight: 600 }}>8 dimensions of wellbeing</span> to help people live better — while growing your reach and impact.
          </p>
          <p
            className="mt-4 text-[15px] sm:text-[16px] font-semibold italic"
            style={{ color: INDIGO_2, fontFamily: 'Outfit, sans-serif' }}
            data-testid="collaborate-tagline"
          >
            Let&apos;s navigate wellness together.
          </p>
        </div>

        {/* 8 DIMENSION CHIPS */}
        <div className="mt-12 md:mt-16" data-testid="collaborate-dimensions">
          <div className="text-center text-[11px] font-bold uppercase tracking-[0.24em]" style={{ color: SLATE_3 }}>
            The 8 dimensions of wellbeing
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5">
            {DIMENSIONS.map((d) => {
              const Ico = d.icon;
              return (
                <div
                  key={d.key}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white text-[13px] font-semibold transition-transform hover:-translate-y-0.5"
                  style={{ border: `1px solid ${BORDER}`, color: NAVY, fontFamily: 'Outfit, sans-serif' }}
                >
                  <span className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: d.bg }}>
                    <Ico className="w-3.5 h-3.5" style={{ color: d.color }} strokeWidth={2.2} />
                  </span>
                  {d.label}
                </div>
              );
            })}
          </div>
        </div>

        {/* WHY COLLABORATE */}
        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: Users,       title: 'Meet aligned users',    body: 'People arrive already assessed — you spend time helping, not qualifying.' },
            { icon: Compass,     title: 'Focus where you fit',   body: 'Choose the dimension(s) you specialise in — we route the right people to you.' },
            { icon: ShieldCheck, title: 'Curated, not crowded',  body: 'Clinity is a considered network. Quality and trust over volume.' },
          ].map((c, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1"
              style={{ border: `1px solid ${BORDER}`, boxShadow: '0 2px 6px rgba(15,23,42,0.04)' }}
              onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 20px 40px -18px rgba(15,23,42,0.15)')}
              onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 2px 6px rgba(15,23,42,0.04)')}
              data-testid={`collaborate-why-${i}`}
            >
              <div className="w-11 h-11 rounded-xl mb-4 flex items-center justify-center" style={{ background: '#EEF2FF' }}>
                <c.icon className="w-5 h-5" style={{ color: INDIGO }} strokeWidth={2.1} />
              </div>
              <div className="text-[15px] font-bold" style={{ color: NAVY, fontFamily: 'Outfit, sans-serif' }}>{c.title}</div>
              <div className="mt-1.5 text-[13px] leading-[1.6]" style={{ color: SLATE_2 }}>{c.body}</div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 md:mt-20 text-center">
          <div
            className="mx-auto max-w-2xl rounded-[28px] px-8 py-12 md:px-12 md:py-14 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #0F172A 0%, #1E1B4B 60%, #0F0F23 100%)',
              boxShadow: '0 40px 80px -30px rgba(15,23,42,0.55)',
            }}
          >
            <div
              aria-hidden="true"
              className="absolute -top-16 -right-16 w-72 h-72 rounded-full blur-3xl opacity-40 pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.5) 0%, transparent 70%)' }}
            />
            <p className="relative text-[12px] font-semibold uppercase tracking-[0.28em] text-[#C7D2FE]">Join the ecosystem</p>
            <h2
              className="relative mt-3 text-[26px] sm:text-[32px] font-bold text-white leading-tight max-w-lg mx-auto"
              style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em' }}
              data-testid="collaborate-cta-headline"
            >
              Bring your expertise to Clinity.
            </h2>
            <div className="relative mt-8">
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleCollaborate}
                data-testid="collaborate-cta-btn"
                className="group inline-flex items-center justify-center gap-3 px-9 py-4 sm:px-11 sm:py-[18px] rounded-full font-semibold text-[15px] sm:text-base text-white no-underline transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0"
                style={{
                  background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                  boxShadow: '0 20px 40px -12px rgba(37,211,102,0.55), inset 0 1px 0 rgba(255,255,255,0.18)',
                  letterSpacing: '-0.005em',
                }}
              >
                <MessageCircle className="w-[18px] h-[18px]" strokeWidth={2.2} />
                Collaborate With Us
                <ArrowRight className="w-[18px] h-[18px] transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2.2} />
              </a>
              <p className="mt-4 text-xs text-white/60">A short WhatsApp intro. We&apos;ll take it from there.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
